import React, { useCallback, useEffect, useState } from 'react';
import { fetchNetworkAlerts, InventoryApiError } from '../../services/inventoryApi';
import './NetworkAlerts.css';

interface NetworkAlertsProps {
  selectedStore?: string;
  enterpriseId: string;
}

interface Alert {
  alertId: string;
  type: string; // e.g. 'supplier_degraded' | 'stockout_risk'
  severity?: 'low' | 'medium' | 'high' | 'critical';
  supplierId?: string;
  supplierName?: string;
  ingredientId?: string;
  ingredientName?: string;
  affectedStores?: number;
  totalStores?: number;
  affectedPercent?: number;
  message?: string;
  createdAt?: string;
  actionLink?: string;
}

const NetworkAlerts: React.FC<NetworkAlertsProps> = () => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchNetworkAlerts();
      setAlerts(res?.alerts || res?.data || []);
    } catch (e) {
      setError(e instanceof InventoryApiError ? e.message : 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="network-alerts">
      <div className="network-alerts__header">
        <div>
          <h2>Network Alerts</h2>
          <p className="network-alerts__sub">
            Cross-store supplier-degraded and stockout-risk signals
          </p>
        </div>
        <button
          className="btn-modern btn-modern-secondary"
          onClick={load}
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error && <div className="network-alerts__error">{error}</div>}
      {loading && <div className="network-alerts__state">Loading alerts...</div>}
      {!loading && !error && alerts.length === 0 && (
        <div className="network-alerts__state">No active alerts. Network looks healthy.</div>
      )}

      {!loading && alerts.length > 0 && (
        <div className="network-alerts__list">
          {alerts.map((a) => {
            const pct =
              a.affectedPercent != null
                ? a.affectedPercent
                : a.affectedStores && a.totalStores
                ? (a.affectedStores / a.totalStores) * 100
                : null;
            return (
              <div
                key={a.alertId}
                className={`alert-card alert-card--${a.severity || 'medium'}`}
              >
                <div className="alert-card__top">
                  <span className="alert-card__type">{a.type.replace(/_/g, ' ')}</span>
                  <span className={`alert-card__severity alert-card__severity--${a.severity || 'medium'}`}>
                    {a.severity || 'medium'}
                  </span>
                </div>
                <div className="alert-card__title">
                  {a.supplierName && <strong>{a.supplierName}</strong>}
                  {a.ingredientName && <strong>{a.ingredientName}</strong>}
                </div>
                {a.message && <div className="alert-card__msg">{a.message}</div>}
                <div className="alert-card__meta">
                  {pct != null && (
                    <span>
                      {pct.toFixed(0)}% of stores
                      {a.affectedStores != null && a.totalStores != null && (
                        <> ({a.affectedStores}/{a.totalStores})</>
                      )}
                    </span>
                  )}
                  {a.createdAt && <span>{new Date(a.createdAt).toLocaleString()}</span>}
                </div>
                {a.actionLink && (
                  <a
                    href={a.actionLink}
                    className="alert-card__action"
                    target="_blank"
                    rel="noreferrer"
                  >
                    View details
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NetworkAlerts;
